import { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { useAppStore } from "@/lib/store";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toBengaliNumerals, toEnglishNumerals } from "@/lib/bdNumerals";
import { parseDDMMYYYY, formatDDMMYYYY } from "@/lib/calculators/age";
import { cn } from "@/lib/utils";

interface Props {
  label: string;
  value: Date | null;
  onChange: (d: Date | null) => void;
  error?: string | null;
  fromYear?: number;
  toDate?: Date;
  placeholder?: string;
}

export function DateField({
  label,
  value,
  onChange,
  error,
  fromYear = 1900,
  toDate = new Date(),
  placeholder = "DD/MM/YYYY",
}: Props) {
  const lang = useAppStore((s) => s.lang);
  const [text, setText] = useState<string>(value ? formatDDMMYYYY(value) : "");
  const [open, setOpen] = useState(false);

  const display = lang === "bn" ? toBengaliNumerals(text) : text;

  const sync = (raw: string) => {
    const ascii = toEnglishNumerals(raw);
    setText(ascii);
    if (!ascii.trim()) {
      onChange(null);
      return;
    }
    const parsed = parseDDMMYYYY(ascii);
    if (parsed) onChange(parsed);
  };

  const onCalendarSelect = (d: Date | undefined) => {
    if (d) {
      onChange(d);
      setText(formatDDMMYYYY(d));
      setOpen(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{label}</label>
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          value={display}
          placeholder={placeholder}
          onChange={(e) => sync(e.target.value)}
          className={cn(
            "w-full rounded-lg border bg-muted px-3 py-2.5 pr-12 text-base outline-none transition tabular-nums focus:bg-background focus:ring-2",
            error
              ? "border-destructive focus:border-destructive focus:ring-destructive/20"
              : "border-border focus:border-accent focus:ring-accent/20",
          )}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="absolute right-1.5 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-background hover:text-foreground"
              aria-label="Open date picker"
            >
              <CalendarIcon size={16} />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={value ?? undefined}
              onSelect={onCalendarSelect}
              defaultMonth={value ?? new Date()}
              captionLayout="dropdown"
              startMonth={new Date(fromYear, 0)}
              endMonth={toDate}
              disabled={(d) => d > toDate || d < new Date(fromYear, 0, 1)}
              autoFocus
              className={cn("p-3 pointer-events-auto")}
            />
          </PopoverContent>
        </Popover>
      </div>
      {value && (
        <p className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          {lang === "bn" ? toBengaliNumerals(format(value, "d MMMM yyyy")) : format(value, "d MMMM yyyy")}
        </p>
      )}
      {error && <p className={cn("text-xs text-destructive", lang === "bn" && "font-bn")}>{error}</p>}
    </div>
  );
}
